import { UpdateResolver } from '@urql/exchange-graphcache';
import {
  BulkCreateSheetShapesMutation,
  BulkCreateSheetShapesMutationVariables,
  BulkDeleteSheetShapesMutation,
  BulkDeleteSheetShapesMutationVariables,
  BulkUpdateSheetShapesMutation,
  BulkUpdateSheetShapesMutationVariables,
  CreateSheetShapeMutation,
  CreateSheetShapeMutationVariables,
  DeleteSheetShapeMutation,
  DeleteSheetShapeMutationVariables,
} from '../../gql/graphql';

export const createSheetShapeResolver: UpdateResolver = (
  result: CreateSheetShapeMutation,
  args: CreateSheetShapeMutationVariables,
  cache
) => {
  if (result.createSheetShape?.sheetShape) {
    const sheetShapesForProject = cache.resolve(
      {
        __typename: 'Project',
        id: result.createSheetShape.sheetShape.projectId,
      },
      'sheetShapes'
    ) as string[] | null;
    const sheetShapeToAdd = cache.keyOfEntity({
      __typename: 'SheetShape',
      id: result.createSheetShape.sheetShape.id,
    });
    if (sheetShapesForProject && sheetShapeToAdd) {
      sheetShapesForProject.push(sheetShapeToAdd);
      cache.link(
        {
          __typename: 'Project',
          id: result.createSheetShape.sheetShape.projectId,
        },
        'sheetShapes',
        sheetShapesForProject
      );
    }
  }
};

export const deleteSheetShapeResolver: UpdateResolver = (
  result: DeleteSheetShapeMutation,
  args: DeleteSheetShapeMutationVariables,
  cache
) => {
  if (result.deleteSheetShape?.sheetShape) {
    const sheetShapesForProject = cache.resolve(
      {
        __typename: 'Project',
        id: result.deleteSheetShape.sheetShape.projectId,
      },
      'sheetShapes'
    ) as string[] | null;
    const sheetShapeToRemove = cache.keyOfEntity({
      __typename: 'SheetShape',
      id: result.deleteSheetShape.sheetShape.id,
    });
    if (sheetShapesForProject && sheetShapeToRemove) {
      cache.link(
        {
          __typename: 'Project',
          id: result.deleteSheetShape.sheetShape.projectId,
        },
        'sheetShapes',
        sheetShapesForProject.filter((key) => key !== sheetShapeToRemove)
      );
    }
  }
};

export const bulkDeleteSheetShapesResolver: UpdateResolver = (
  result: BulkDeleteSheetShapesMutation,
  args: BulkDeleteSheetShapesMutationVariables,
  cache
) => {
  const deletedShapes = result.bulkDeleteSheetShapes?.sheetShapes;
  if (!deletedShapes || deletedShapes.length === 0) return;

  // Group deleted shapes by projectId for efficient cache updates
  const shapesByProject = deletedShapes.reduce((acc, shape) => {
    if (!acc[shape.projectId]) {
      acc[shape.projectId] = [];
    }
    acc[shape.projectId].push(shape.id);
    return acc;
  }, {} as Record<string, string[]>);

  // Update cache for each project
  for (const [projectId, deletedIds] of Object.entries(shapesByProject)) {
    const sheetShapesForProject = cache.resolve(
      { __typename: 'Project', id: projectId },
      'sheetShapes'
    ) as string[] | null;

    if (sheetShapesForProject) {
      const keysToRemove = new Set(
        deletedIds.map((id) =>
          cache.keyOfEntity({ __typename: 'SheetShape', id })
        )
      );
      cache.link(
        { __typename: 'Project', id: projectId },
        'sheetShapes',
        sheetShapesForProject.filter((key) => !keysToRemove.has(key))
      );
    }
  }
};

export const bulkCreateSheetShapesResolver: UpdateResolver = (
  result: BulkCreateSheetShapesMutation,
  args: BulkCreateSheetShapesMutationVariables,
  cache
) => {
  const createdShapes = result.bulkCreateSheetShapes?.sheetShapes;
  if (!createdShapes || createdShapes.length === 0) return;

  // Group created shapes by projectId for efficient cache updates
  const shapesByProject = createdShapes.reduce((acc, shape) => {
    if (!acc[shape.projectId]) {
      acc[shape.projectId] = [];
    }
    acc[shape.projectId].push(shape.id);
    return acc;
  }, {} as Record<string, string[]>);

  // Update cache for each project
  for (const [projectId, createdIds] of Object.entries(shapesByProject)) {
    const sheetShapesForProject = cache.resolve(
      { __typename: 'Project', id: projectId },
      'sheetShapes'
    ) as string[] | null;

    if (sheetShapesForProject) {
      const keysToAdd = createdIds
        .map((id) => cache.keyOfEntity({ __typename: 'SheetShape', id }))
        .filter((key): key is string => key !== null);
      cache.link({ __typename: 'Project', id: projectId }, 'sheetShapes', [
        ...sheetShapesForProject,
        ...keysToAdd,
      ]);
    }
  }
};

// Bulk update resolver - the normalized cache automatically updates shape data
// since we return the full SheetShapeDeep fragment. This resolver is here for consistency.
export const bulkUpdateSheetShapesResolver: UpdateResolver = (
  _result: BulkUpdateSheetShapesMutation,
  _args: BulkUpdateSheetShapesMutationVariables,
  _cache
) => {
  // No-op: URQL's normalized cache automatically updates entities
  // when we return data with matching __typename and id
};
